<script setup lang="ts">
/**
 * NoteActionsOverlay
 *
 * **Aktionsmenü einer Note und einziger Host der Takt-Editoren**, auf **beiden** Vorschauen –
 * Notenschrift (Eingabe) UND Griffschrift (Ausgabe). Geöffnet per Rechtsklick, Doppelklick oder
 * über die Takt-Buttons der `ScoreEditToolbar` (der Host `MusicXmlPreview` öffnet es, sobald
 * `activeTab` einen Editor meldet).
 *
 * **Zwei Ansichten, gesteuert allein über `activeTab` des `ScoreEditContext`:** `'note'` = Menü,
 * `chord`/`key`/`time`/`structure`/`measures`/`bass` = der jeweilige Editor (`ChordEditView` …) in
 * derselben Karte, mit „← Menü" und ×. Schließen setzt `activeTab` auf `'note'` zurück, die Auswahl
 * bleibt stehen. Die Position (Cursor bzw. im Editor-Modus die gewählte Note) liefert der Host als
 * Anker.
 *
 * **Das Menü ist kontextabhängig – die Prop `scope` sagt, WOHIN geklickt wurde:**
 * - **`'note'`** (Klick traf eine Note): Info-Zeile · Werkzeug-Launcher · Noten-Edits ·
 *   Takt-Editoren als abgesetzter letzter Block.
 * - **`'measure'`** (Klick in die freie Taktfläche): Takt-Kopf („Takt 7") · **nur** die
 *   Takt-Editoren. Alles Noten-Bezogene fiele hier auf die Note zurück, die der Takt-Watch
 *   automatisch nachgewählt hat – nicht auf eine, die jemand angeklickt hätte.
 *
 * Welche Blöcke das im Einzelfall sind, entscheidet **nicht** dieses Template, sondern der reine
 * Helfer {@link ./noteActionsMenu!noteActionsMenu} – nur dort ist es testbar (das Repo mountet
 * keine Komponenten). Er liefert auch die Trennlinien (`block.separator`), damit keine führende
 * oder doppelte Linie mehr entstehen kann.
 *
 * Die Blöcke im Einzelnen:
 * 1. **Info-Zeile** mit der Tonhöhe der angeklickten Note. Auf der Griffschrift-Ausgabe
 *    (`showGrip`) steht dort **nur der tatsächlich klingende Ton** (`sounding_notes` aus
 *    `POST /api/note-grips`) – der *notierte* Wert wäre dort die Griffschrift-Staff-Position und
 *    damit gerade **nicht** der Ton, den der Knopf erzeugt. Auf der Eingabe-Vorschau ist der
 *    notierte Wert der klingende, er kommt direkt aus der `NoteInfo` (kein Request).
 * 2. **Werkzeug-Launcher** „Griff ändern" (`showGrip` → `open`), „Bass bearbeiten"
 *    (`showBass` → `openBass`), „Gesangstext" (`showLyric` → `openLyric`). Den vollständigen
 *    {@link NoteGripDialog} bzw. das `BassLyricPopover` öffnet weiterhin der Parent
 *    (`MusicXmlPreview`), der beide ohnehin für den Toolbar-Weg besitzt. „Griff ändern" entfällt
 *    auf einer **Pause** (Spiegel des Toolbar-Gates `canEditGrip` – eine Pause hat keinen Griff).
 * 3. **Noten-Edits** (Teilen / Zusammenführen / Löschen) direkt aus dem `ScoreEditContext` –
 *    dasselbe Injection-Muster wie die `ScoreEditToolbar`, daher **keine** zusätzlichen
 *    Props/Emits über den Parent.
 * 4. **Takt-Editoren** (Liste aus `measureEditEntries`): rufen `open…Editor` aus dem
 *    `ScoreEditContext` (lädt den aktuellen Wert, setzt `activeTab`) – die Karte wechselt damit in
 *    die Editor-Ansicht. Ausgeblendet über `showMeasureEdits` (Ausgabe-Vorschau).
 *
 * Backdrop-Klick / ESC (Capture-Phase, damit die ScoreEditToolbar-Escape nicht parallel die
 * Notenauswahl aufhebt) schließen das Menü.
 */
import {computed, ref, watch} from 'vue';
import {useElementSize} from '@vueuse/core';
import {Icon} from '@iconify/vue';

import {useCaptureEscape} from '@/composables/useCaptureEscape';
import type {NoteInfo} from '@/composables/musicXmlDom';
import {useNoteGrips} from '@/composables/useNoteGrips';

import SplitNoteIcon from './icons/SplitNoteIcon.vue';
import MergeNotesIcon from './icons/MergeNotesIcon.vue';
import {injectScoreEditContext} from './editContext';
import {notePitchLabel} from './notePitchLabel';
import {measureEditEntries, measureEditEntry, type MeasureEditTab} from './measureEditEntries';
import {type ActionScope, noteActionsMenu, type NoteActionsMenu} from './noteActionsMenu';
import ChordEditView from './ChordEditView.vue';
import KeySignatureEditView from './KeySignatureEditView.vue';
import TimeSignatureEditView from './TimeSignatureEditView.vue';
import StructureEditView from './StructureEditView.vue';
import MeasuresEditView from './MeasuresEditView.vue';
import BassStyleEditView from './BassStyleEditView.vue';

const {
  anchorX = 0,
  anchorY = 0,
  showGrip = true,
  showBass = false,
  showLyric = false,
  showMeasureEdits = true,
  scope = 'note',
  musicXml = undefined,
  selectedNote = null,
} = defineProps<{
  /** Anker in Viewport-Koordinaten: Cursor (Menü) bzw. gewählte Note (Editor), vom Host berechnet. */
  anchorX?: number;
  anchorY?: number;
  /** „Griff ändern" anbieten (Griffschrift-Ausgabe mit enableGrips). Steuert zugleich, dass die
   *  Info-Zeile den KLINGENDEN statt des notierten Tons zeigt. */
  showGrip?: boolean;
  /** „Bass bearbeiten" anbieten (Griffschrift-Ausgabe mit enableBassEdit). */
  showBass?: boolean;
  /** „Gesangstext" anbieten (beide Vorschauen, mit enableLyricEdit). */
  showLyric?: boolean;
  /** Takt-Editoren (Akkord/Tonart/Taktart/Bass-Spielweise) anbieten. Folgt `showAdvancedTools`
   *  der Vorschau, damit die aufgeräumte Griffschrift-Ausgabe sie wie die Toolbar ausblendet. */
  showMeasureEdits?: boolean;
  /** Geltungsbereich des letzten Klicks: `'note'` = Note getroffen, `'measure'` = freie
   *  Taktfläche. Entscheidet, welche Blöcke das Menü zeigt. */
  scope?: ActionScope;
  /** Aktuelles MusicXML – nur für die Griff-Abfrage der Info-Zeile (Ausgabe). */
  musicXml?: string | null | undefined;
  /** Die per Rechtsklick gewählte Note – Quelle der Info-Zeile. */
  selectedNote?: NoteInfo | null;
}>();

const emit = defineEmits<{
  open: [];
  openBass: [];
  openLyric: [];
}>();

const isOpen = defineModel<boolean>('isOpen', {default: false, type: Boolean});

// Edit-Aktionen aus der Vorschau (Muster: ScoreEditToolbar). `null`-Fallback, damit ein
// isolierter Unit-Mount ohne Provider nicht wirft – die Edit-Einträge fehlen dann schlicht.
const editContext = injectScoreEditContext(null);

// ---- Info-Zeile ----------------------------------------------------------------------------

// Auf der Ausgabe den klingenden Ton laden. Der Getter ist auf `isOpen` gegatet, damit NUR beim
// geöffneten Menü ein Request läuft (das Composable feuert sonst bei jeder Notenauswahl), und
// zusätzlich auf den Noten-Bereich: im Takt-Bereich gibt es gar keine Info-Zeile, ein Request für
// die vom Takt-Watch nachgewählte Note wäre reine Verschwendung.
// Pausen filtert `buildNoteGripsRequest` selbst heraus → kein Request.
const {response, loading} = useNoteGrips(
    () => musicXml ?? undefined,
    () => (isOpen.value && showGrip && scope === 'note' ? selectedNote : null),
);

// Ableitung als reiner Helfer (unit-getestet in `noteActionsOverlay.spec.ts`).
const pitchLabel = computed(() => notePitchLabel({
  note: selectedNote,
  preferSounding: showGrip,
  soundingNotes: response.value?.sounding_notes ?? [],
  loading: loading.value,
}));

/** Auf der Ausgabe ist der angezeigte Ton der klingende (nicht der notierte) – im Tooltip sagen. */
const pitchTitle = computed(() => (showGrip ? 'Klingender Ton' : 'Notierte Tonhöhe'));

// ---- Noten-Edits ---------------------------------------------------------------------------

// Guards analog ScoreEditToolbar: `hasNotes` = irgendeine Note gewählt, `isSingle` = genau eine.
// Nach einem Rechtsklick ist die Auswahl immer einelementig, die Guards bleiben trotzdem korrekt.
const selectionCount = computed(() => editContext?.selectedNotes.value.length ?? 0);
const hasNotes = computed(() => selectionCount.value > 0);
const isSingle = computed(() => selectionCount.value === 1 && !!editContext?.selectedNote.value);

function close(): void {
  isOpen.value = false;
}

/** Edit ausführen und das Menü schließen (wie ein Klick auf den Toolbar-Button). */
function runEdit(action: (note: NoteInfo) => void): void {
  const note = editContext?.selectedNote.value;
  if (!note) return;
  action(note);
  close();
}

function splitNote(): void {
  if (!isSingle.value) return;
  runEdit((note) => editContext?.splitNote(note));
}

function mergeNote(): void {
  if (!isSingle.value) return;
  runEdit((note) => editContext?.mergeNote(note));
}

/** Haltebögen der ganzen (Mehrfach-)Auswahl zusammenfassen und das Menü schließen. */
function mergeTiedNotes(): void {
  if (!hasNotes.value) return;
  editContext?.mergeTiedNotes();
  close();
}

/** Länge der ganzen (Mehrfach-)Auswahl halbieren/verdoppeln (Q/W) und das Menü schließen. */
function scaleDurations(factor: 'halve' | 'double'): void {
  if (!hasNotes.value) return;
  if (factor === 'halve') editContext?.halveDurations();
  else editContext?.doubleDurations();
  close();
}

function deleteSelection(): void {
  if (!hasNotes.value) return;
  editContext?.requestDelete();
  close();
}

// ---- Takt-Editoren ------------------------------------------------------------------------

const measureEntries = computed(() => (editContext && showMeasureEdits
    ? measureEditEntries({hasBassPreview: editContext.hasBassPreview.value})
    : []));
const hasMeasure = computed(() => editContext?.selectedMeasure.value != null);

// ---- Sichtbarkeit: EINE Quelle, rein und unit-getestet --------------------------------------

// Welche Blöcke das Menü zeigt, entscheidet `noteActionsMenu` – inklusive der Trennlinien. Das
// Template liest nur noch `menu.blocks`; die früheren `v-if="showGrip || showBass || …"`-Ketten
// vor jedem Separator sind damit weg (sie waren die Quelle führender/doppelter Linien).
// oldValue-Muster (Pflicht für objekt-liefernde Computeds, siehe VUE_RULES.md).
const menu = computed<NoteActionsMenu>((oldValue) => {
  const next = noteActionsMenu({
    scope,
    showGrip,
    showBass,
    showLyric,
    hasEditContext: editContext !== null,
    pitchLabel: pitchLabel.value,
    selectedNote,
    measureNumber: editContext?.selectedMeasure.value ?? null,
    measureEntries: measureEntries.value,
  });
  if (oldValue && JSON.stringify(oldValue) === JSON.stringify(next)) return oldValue;
  return next;
});

/** Editor in derselben Karte öffnen: `open…Editor` lädt den aktuellen Wert und setzt `activeTab`. */
function openMeasureEditor(tab: MeasureEditTab): void {
  if (!editContext || !hasMeasure.value) return;
  const open = {
    chord: editContext.openChordEditor,
    key: editContext.openKeySignatureEditor,
    time: editContext.openTimeSignatureEditor,
    structure: editContext.openStructureEditor,
    measures: editContext.openMeasuresEditor,
    bass: editContext.openBassStyleEditor,
  }[tab];
  open();
}

// ---- Editor-Ansicht ------------------------------------------------------------------------

// Aktiver Editor (`null` = Menü). Bass-Spielweise nur mit Bass-System, sonst zurück aufs Menü.
const activeEntry = computed(() => {
  if (!editContext || !hasMeasure.value) return null;
  const entry = measureEditEntry(editContext.activeTab.value);
  if (entry?.name === 'bass' && !editContext.hasBassPreview.value) return null;
  return entry;
});

const EDITOR_VIEWS = {
  chord: ChordEditView,
  key: KeySignatureEditView,
  time: TimeSignatureEditView,
  structure: StructureEditView,
  measures: MeasuresEditView,
  bass: BassStyleEditView,
} as const;
const editorView = computed(() => (activeEntry.value ? EDITOR_VIEWS[activeEntry.value.name] : null));

function backToMenu(): void {
  if (editContext) editContext.activeTab.value = 'note';
}

// Jedes Schließen – auch vom Host (Launcher „Griff ändern" usw.) – führt zurück aufs Menü, damit der
// nächste Toolbar-Klick `activeTab` wieder ändert und das Overlay erneut öffnet.
watch(isOpen, (open) => {
  if (!open) backToMenu();
});

// ---- Positionierung ------------------------------------------------------------------------

// Viewport-Klemmung über die GEMESSENE Kartengröße – Menü und Editor sind sehr unterschiedlich
// groß, Schätzwerte schnitten den Editor am Rand ab.
const MARGIN = 8;
const cardRef = ref<HTMLElement | null>(null);
const {width: cardWidth, height: cardHeight} = useElementSize(cardRef, undefined, {box: 'border-box'});

const launcherStyle = computed(() => {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const left = Math.min(Math.max(MARGIN, anchorX), Math.max(MARGIN, vw - cardWidth.value - MARGIN));
  const top = Math.min(Math.max(MARGIN, anchorY), Math.max(MARGIN, vh - cardHeight.value - MARGIN));
  return {left: `${left}px`, top: `${top}px`};
});

// Styling als Klassen-Literal statt `@apply` im scoped-Style: Tailwind v4 generiert Utilities nur
// aus dem Datei-Scan – ein ausschliesslich via `@apply` genutztes Utility waere eine „unknown
// utility" im Build (siehe Kommentar in `ScoreEditToolbar.vue`). Das Literal wird mitgescannt.
const ITEM_CLASS = 'inline-flex items-center gap-2 px-3 py-1.5 text-left text-sm font-medium outline-none hover:bg-gray-50 focus-visible:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300/80  disabled:opacity-80 disabled:hover:bg-transparent';

// ESC (Capture-Phase, nur solange sichtbar) schließt das Menü; der geöffnete Dialog hat sein
// eigenes Capture-ESC.
useCaptureEscape(isOpen, close);
</script>

<template>
  <!-- Menü-Ebene: transparenter Ganzflächen-Backdrop (Klick/Rechtsklick daneben schließt) +
       Menükarte am Cursor. -->
  <Transition
      enter-active-class="animate-fade animate-duration-150"
      leave-active-class="transition-opacity duration-100"
      leave-to-class="opacity-0"
  >
  <div
      v-if="isOpen"
      class="fixed inset-0 z-50"
      @click.self="close"
      @contextmenu.self.prevent="close"
  >
  <div
      ref="cardRef"
      class="fixed flex min-w-44 max-h-[calc(100dvh-1rem)] max-w-[calc(100vw-1rem)] flex-col items-stretch overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg transition-[left,top] duration-200"
      :style="launcherStyle"
  >
  <!-- Editor-Ansicht: Kopf (← Menü · Titel · ×) + Editor des Takts. -->
  <template v-if="activeEntry && editorView">
    <div class="flex items-center gap-1 border-b border-gray-200 px-1 pb-1">
      <button
          type="button"
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          title="Zurück zum Menü"
          aria-label="Zurück zum Menü"
          @click="backToMenu"
      >
        <Icon icon="fa6-solid:arrow-left"/>
      </button>
      <div class="flex flex-1 items-center gap-1.5 text-sm font-semibold text-gray-700">
        <Icon :icon="activeEntry.icon" :class="activeEntry.colorClass"/>
        {{ activeEntry.label }}
      </div>
      <button
          type="button"
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          title="Schließen"
          aria-label="Schließen"
          @click="close"
      >
        <Icon icon="fa6-solid:xmark"/>
      </button>
    </div>
    <div class="min-h-0 overflow-auto">
      <KeepAlive :max="10">
        <component :is="editorView"/>
      </KeepAlive>
    </div>
  </template>

  <!-- Menü-Ansicht: die sichtbaren Blöcke und ihre Trennlinien kommen aus `noteActionsMenu`
       (Geltungsbereich + Flags), nicht aus v-if-Ketten im Template. -->
  <template v-else>
  <template v-for="block in menu.blocks" :key="block.name">
    <div v-if="block.separator" class="my-1 border-t border-gray-100"/>

    <!-- Tonhöhe der angeklickten Note (Ausgabe: klingender Ton, Eingabe: notierter Wert). -->
    <div
        v-if="block.name === 'info'"
        class="flex items-center gap-2 border-b border-gray-500 px-3 pb-1.5 pt-1 text-sm font-semibold text-gray-900"
        :title="pitchTitle"
    >
      <Icon icon="game-icons:piano-keys"/>
      {{ pitchLabel }}
    </div>

    <!-- Takt-Bereich: sagt, WELCHER Takt gemeint ist – es gibt hier keine Note als Bezug. -->
    <div
        v-else-if="block.name === 'measureHeader'"
        class="flex items-center gap-2 border-b border-gray-500 px-3 pb-1.5 pt-1 text-sm font-semibold text-gray-900"
        title="Dieser Takt"
    >
      <Icon icon="fa6-solid:grip-lines-vertical"/>
      {{ menu.measureHeader }}
    </div>

    <!-- Werkzeug-Launcher (der Parent hostet die Dialoge). -->
    <template v-else-if="block.name === 'tools'">
      <button
          v-if="menu.showGripItem"
          type="button"
          :class="ITEM_CLASS"
          class="text-emerald-600"
          @click="emit('open')"
      >
        <Icon icon="fa6-solid:hand"/>
        Griff ändern
      </button>
      <button
          v-if="menu.showBassItem"
          type="button" class="text-indigo-600"
          :class="ITEM_CLASS"
          @click="emit('openBass')"
      >
        <Icon icon="game-icons:f-clef"/>
        Bass bearbeiten
      </button>
      <button
          v-if="menu.showLyricItem"
          type="button"
          class="text-purple-600"
          :class="ITEM_CLASS"
          @click="emit('openLyric')"
      >
        <Icon icon="ic:baseline-lyrics"/>
        Lyrics
      </button>
    </template>

    <!-- Noten-Edits (beide Vorschauen). -->
    <template v-else-if="block.name === 'noteEdits'">
      <button
          type="button"
          :class="ITEM_CLASS"
          :disabled="!isSingle"
          title="Note/Pause teilen (Strg+Umschalt+S)"
          @click="splitNote"
      >
        <SplitNoteIcon class="text-gray-500"/>
        Teilen
      </button>
      <button
          type="button"
          :class="ITEM_CLASS"
          :disabled="!isSingle"
          title="Note/Pause mit der nächsten zusammenführen (Strg+Umschalt+M)"
          @click="mergeNote"
      >
        <MergeNotesIcon class="text-gray-500"/>
        Zusammenführen
      </button>
      <button
          v-if="showMeasureEdits"
          type="button"
          :class="ITEM_CLASS"
          :disabled="!hasNotes"
          title="Haltebögen der Auswahl zusammenfassen (Strg+Umschalt+H)"
          @click="mergeTiedNotes"
      >
        <Icon icon="lucide:link-2" class="text-gray-500"/>
        Haltebögen zusammenfassen
      </button>
      <!-- Q/W nur auf der Eingabe (wie in der Toolbar): der Reflow verschöbe in der Ausgabe die Melodie
           gegen Druck-Markierungen und Bass-Träger. -->
      <button
          v-if="showMeasureEdits"
          type="button"
          :class="ITEM_CLASS"
          :disabled="!hasNotes"
          title="Länge halbieren (Q)"
          @click="scaleDurations('halve')"
      >
        <Icon icon="lucide:fold-horizontal" class="text-gray-500"/>
        Länge halbieren
      </button>
      <button
          v-if="showMeasureEdits"
          type="button"
          :class="ITEM_CLASS"
          :disabled="!hasNotes"
          title="Länge verdoppeln (W)"
          @click="scaleDurations('double')"
      >
        <Icon icon="lucide:unfold-horizontal" class="text-gray-500"/>
        Länge verdoppeln
      </button>
      <button
          type="button" class="text-red-500"
          :class="ITEM_CLASS"
          :disabled="!hasNotes"
          title="Löschen (Entf)"
          @click="deleteSelection"
      >
        <Icon icon="fa6-solid:trash"/>
        Note löschen
      </button>
    </template>

    <!-- Takt-Editoren: wechseln diese Karte in die Editor-Ansicht. -->
    <template v-else>
      <button
          v-for="entry in measureEntries"
          :key="entry.name"
          type="button"
          :class="[ITEM_CLASS, entry.colorClass]"
          :disabled="!hasMeasure"
          :title="`${entry.label} dieses Takts bearbeiten`"
          @click="openMeasureEditor(entry.name)"
      >
        <Icon :icon="entry.icon"/>
        {{ entry.label }}…
      </button>
    </template>
  </template>
  </template>
  </div>
  </div>
  </Transition>
</template>

